import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface LibraryRelease {
  id: string;
  slug: string;
  name: string;
  short_description: string | null;
  brand_slug: string | null;
  content_type: 'materials' | 'stratigraphies' | 'bundle';
  version_year: number;
  version_suffix: string | null;
  price_eur: number;
  upgrade_price_eur: number | null;
  status: 'draft' | 'published' | 'archived';
  is_active: boolean;
  is_current_year: boolean;
  lemonsqueezy_variant_id: string | null;
  includes_release_slugs: string[] | null;
  sort_order: number;
  materials_count: number;
  stratigraphies_count: number;
  created_at: string;
  published_at: string | null;
}

export function useAdminReleases() {
  return useQuery<LibraryRelease[]>({
    queryKey: ['admin', 'releases-rpc'],
    staleTime: 15_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_get_all_releases');
      if (error) throw error;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return ((data ?? []) as any[]).map((r) => ({
        ...r,
        materials_count: Number(r.materials_count ?? 0),
        stratigraphies_count: Number(r.stratigraphies_count ?? 0),
      })) as LibraryRelease[];
    },
  });
}

export function useAddItemsToRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ releaseId, itemType, itemIds }: { releaseId: string; itemType: 'material' | 'stratigraphy'; itemIds: string[] }) => {
      const { data, error } = await supabase.rpc('admin_add_items_to_release', {
        p_release_id: releaseId,
        p_item_type: itemType,
        p_item_ids: itemIds,
      });
      if (error) throw error;
      return data as number;
    },
    onSuccess: (n, vars) => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['library_addons'] });
      qc.invalidateQueries({ queryKey: ['materials'] });
      qc.invalidateQueries({ queryKey: ['unified-stratigraphies'] });
      toast.success(`Aggiunti ${n} ${vars.itemType === 'material' ? 'materiali' : 'stratigrafie'} alla release`);
    },
    onError: (e: Error) => toast.error('Errore', { description: e.message }),
  });
}

export function useRemoveItemsFromRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ releaseId, itemType, itemIds }: { releaseId: string; itemType: 'material' | 'stratigraphy'; itemIds: string[] }) => {
      const { data, error } = await supabase.rpc('admin_remove_items_from_release', {
        p_release_id: releaseId,
        p_item_type: itemType,
        p_item_ids: itemIds,
      });
      if (error) throw error;
      return data as number;
    },
    onSuccess: (n) => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      toast.success(`Rimossi ${n} elementi dalla release`);
    },
    onError: (e: Error) => toast.error('Errore', { description: e.message }),
  });
}

export function useUpdateRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { id: string; name?: string; short_description?: string; price_eur?: number; upgrade_price_eur?: number | null }) => {
      const { id, ...patch } = payload;
      const { error } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('library_releases' as any)
        .update(patch)
        .eq('id', id);
      if (error) throw error;
      // Sync su library_addons gemellato (lo Store legge da li')
      if (patch.name || patch.short_description || patch.price_eur != null) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: rel } = await supabase.from('library_releases' as any).select('slug').eq('id', id).single();
        if (rel?.slug) {
          await supabase
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .from('library_addons' as any)
            .update(patch)
            .eq('slug', rel.slug);
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['library_addons'] });
      toast.success('Release aggiornata');
    },
    onError: (e: Error) => toast.error('Errore', { description: e.message }),
  });
}

/**
 * Aggiorna includes_release_slugs di un bundle. Sync su library_releases +
 * library_addons gemellato (per coerenza dello Store + webhook expansion).
 *
 * Input: id del bundle + nuovo array di slug puntati (Listino + Strat compresi).
 */
export function useUpdateBundleSlugs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { bundleId: string; slugs: string[] }) => {
      // 1) Recupero slug del bundle (necessario per syncare library_addons)
      const { data: target, error: e0 } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('library_releases' as any)
        .select('slug')
        .eq('id', payload.bundleId)
        .single();
      if (e0 || !target) throw e0 ?? new Error('Bundle non trovato');
      const bundleSlug = (target as { slug: string }).slug;

      // 2) Update library_releases.includes_release_slugs
      const { error: e1 } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('library_releases' as any)
        .update({ includes_release_slugs: payload.slugs })
        .eq('id', payload.bundleId);
      if (e1) throw e1;

      // 3) Sync library_addons gemellato (campo si chiama includes_addon_slugs lì)
      const { error: e2 } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('library_addons' as any)
        .update({ includes_addon_slugs: payload.slugs })
        .eq('slug', bundleSlug);
      if (e2) throw e2;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['library_addons'] });
      toast.success('Bundle aggiornato', {
        description: 'Composizione salvata. I prossimi acquisti useranno la nuova composizione.',
      });
    },
    onError: (e: Error) => toast.error('Errore', { description: e.message }),
  });
}

/**
 * Marca una release come "Corrente" (is_current_year=true) e contemporaneamente
 * de-marca le altre release della stessa famiglia (stesso brand_slug + content_type)
 * — non possono coesistere 2 versioni "correnti" della stessa famiglia.
 *
 * Sync su library_addons gemellato (lo Store legge da lì il flag per pre-selezionare
 * la versione default nel dropdown LibraryCard).
 */
export function useMarkAsCurrentRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (releaseId: string) => {
      // Recupero la release target per sapere brand_slug + content_type
      const { data: target, error: e0 } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('library_releases' as any)
        .select('id, slug, brand_slug, content_type, version_year')
        .eq('id', releaseId)
        .single();
      if (e0 || !target) throw e0 ?? new Error('Release non trovata');

      const t = target as { id: string; slug: string; brand_slug: string | null; content_type: string; version_year: number };

      // 1) De-marca le altre release della stessa famiglia
      const { error: e1 } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('library_releases' as any)
        .update({ is_current_year: false })
        .eq('brand_slug', t.brand_slug)
        .eq('content_type', t.content_type)
        .neq('id', t.id);
      if (e1) throw e1;

      // 2) Marca la release target come corrente
      const { error: e2 } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('library_releases' as any)
        .update({ is_current_year: true })
        .eq('id', t.id);
      if (e2) throw e2;

      // 3) Sync su library_addons gemellato (per famiglia: tutti gli addon dello
      //    stesso brand+category vanno aggiornati per coerenza dropdown nello Store)
      if (t.brand_slug) {
        // Mapping content_type → category in library_addons
        const cat = t.content_type === 'materials' ? 'brand'
                  : t.content_type === 'stratigraphies' ? 'stratigraphies'
                  : 'bundle';
        await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .from('library_addons' as any)
          .update({ is_current_year: false })
          .eq('brand_slug', t.brand_slug)
          .eq('category', cat);
        await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .from('library_addons' as any)
          .update({ is_current_year: true })
          .eq('slug', t.slug);
      }
      return { brand: t.brand_slug, content_type: t.content_type, year: t.version_year };
    },
    onSuccess: (info) => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['library_addons'] });
      toast.success(`Marcata come Corrente: ${info.brand ?? '?'} · ${info.content_type} · ${info.year}`, {
        description: 'Le altre versioni della stessa famiglia non sono più correnti.',
      });
    },
    onError: (e: Error) => toast.error('Errore', { description: e.message }),
  });
}

export function useDeleteRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (releaseId: string) => {
      const { data, error } = await supabase.rpc('admin_delete_release', { p_release_id: releaseId });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['library_addons'] });
      toast.success('Release eliminata');
    },
    onError: (e: Error) => toast.error('Errore eliminazione', { description: e.message }),
  });
}

export function usePublishRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (releaseId: string) => {
      const { data, error } = await supabase.rpc('admin_publish_release', { p_release_id: releaseId });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['library_addons'] });
      toast.success('Release pubblicata nello Store', { description: 'Ora i clienti la vedranno (richiede variant LS collegato per essere acquistabile).' });
    },
    onError: (e: Error) => toast.error('Errore pubblicazione', { description: e.message }),
  });
}

export function useCreateRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      slug: string;
      name: string;
      brand_slug: string;
      content_type: 'materials' | 'stratigraphies' | 'bundle';
      version_year: number;
      version_suffix?: string;
      price_eur?: number;
    }) => {
      const { data, error } = await supabase.rpc('admin_create_release', {
        p_slug: payload.slug,
        p_name: payload.name,
        p_brand_slug: payload.brand_slug,
        p_content_type: payload.content_type,
        p_version_year: payload.version_year,
        p_version_suffix: payload.version_suffix ?? null,
        p_price_eur: payload.price_eur ?? 0,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'releases-rpc'] });
      toast.success('Release creata');
    },
    onError: (e: Error) => toast.error('Errore creazione release', { description: e.message }),
  });
}
